import { useEffect, useState } from "react";
import { Loader2, Send, ShieldAlert, CalendarClock, Gift, Truck, PackageCheck, Ban, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const TILES = [["address", "Warten auf Adresse", MapPin], ["preparing", "Zu versenden", Gift], ["shipped", "Unterwegs", Truck], ["delivered", "Angekommen", PackageCheck], ["forfeited", "Verfallen", Ban]];
const RULES = [
  "Eine Box gibt es für jeweils 30 Tage am Stück mit aktivem Status „Projekt Unterstützer“ (Tag 30, 60, 90 …).",
  "Endet der Status – abgelaufen oder von euch beendet – verfallen alle noch nicht versendeten Boxen automatisch. Der Nutzer wird benachrichtigt.",
  "Wird der Status neu aktiviert, beginnen die 30 Tage von vorn. Bereits versendete Boxen bleiben unberührt.",
];

function FeedCardToggle({ state, onDone }) {
  const [busy, setBusy] = useState(false);
  const on = !!state?.active;
  const toggle = async (active = !on) => {
    setBusy(true);
    try { await api.put("/admin/mystery-box/feed-card", { active }); toast.success(!active ? "Karte aus dem Feed entfernt" : on ? "Karte erscheint jetzt wieder bei allen – auch bei denen, die sie geschlossen hatten 🎁" : "Mystery-Box-Karte wird jetzt allen im Feed angezeigt 🎁"); onDone(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-3xl border border-pink-100 p-5 flex flex-wrap items-center gap-4" data-testid="mystery-feed-toggle">
      <div className="w-14 h-14 rounded-2xl vip-gold-metal flex items-center justify-center shrink-0"><Gift className="w-7 h-7" /></div>
      <div className="flex-1 min-w-[200px]">
        <p className="font-bold">Karte „Nur für Unterstützer – Die monatliche Mystery-Box“ im Feed</p>
        <p className="text-sm text-slate-500">Zeigt die goldene Mystery-Box-Karte von der Unterstützen-Seite oben im Feed aller Mitglieder.{on && state.since && ` Aktiv seit ${new Date(state.since).toLocaleString("de-DE")}.`}</p>
      </div>
      {on && <button onClick={() => toggle(true)} disabled={busy} data-testid="mystery-feed-reshow-btn" className="h-11 px-6 rounded-full font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50 bg-rose-500 text-white"><Send className="w-4 h-4" /> Jetzt erneut bei allen einblenden</button>}
      <button onClick={() => toggle()} disabled={busy} data-testid="mystery-feed-toggle-btn" className={`h-11 px-6 rounded-full font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50 ${on ? "border border-pink-200 text-slate-600 bg-white" : "bg-rose-500 text-white"}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : on ? <Ban className="w-4 h-4" /> : <Send className="w-4 h-4" />} {on ? "Aus dem Feed entfernen" : "Im Feed anzeigen"}
      </button>
    </div>
  );
}

function PromoButton({ last, onDone }) {
  const [busy, setBusy] = useState(false);
  const post = async () => {
    setBusy(true);
    try { await api.post("/admin/mystery-box/promo-post"); toast.success("Mystery-Box-Bild ist jetzt im Feed 🎁"); onDone(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-3xl border border-pink-100 p-5 grid sm:grid-cols-[160px_1fr] gap-4 items-center" data-testid="mystery-promo">
      <img src="/promo/mystery-box.webp" alt="Animiertes Mystery-Box-Bild" className="w-40 h-40 rounded-2xl object-cover shadow-md" data-testid="mystery-promo-preview" />
      <div className="space-y-2">
        <p className="font-bold">Animiertes Werbebild für den Feed</p>
        <p className="text-sm text-slate-500">Postet das glitzernde Mystery-Box-Bild mit Erklärtext als Beitrag von euch in den Feed.</p>
        {last && <p className="text-xs text-slate-400" data-testid="mystery-promo-last">Zuletzt gepostet: {new Date(last).toLocaleString("de-DE")}</p>}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button disabled={busy} data-testid="mystery-promo-post" className="h-11 px-6 rounded-full bg-rose-500 text-white font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} In den Feed schicken</button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Mystery-Box-Bild posten?</AlertDialogTitle><AlertDialogDescription>Der Beitrag erscheint sofort für alle Mitglieder im Feed.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel data-testid="mystery-promo-cancel">Abbrechen</AlertDialogCancel><AlertDialogAction onClick={post} data-testid="mystery-promo-confirm">Jetzt posten</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export function MysteryOverview() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/mystery-overview").then(({ data }) => setD(data)).catch(() => setD({ counts: {}, upcoming: [] }));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-rose-500" /></div>;
  return (
    <div className="space-y-4" data-testid="mystery-overview">
      <div className="rounded-3xl p-5 bg-gradient-to-br from-amber-50 to-rose-50 border border-amber-200 space-y-2" data-testid="mystery-admin-rules">
        <p className="font-bold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-600" /> Regeln der Mystery-Box</p>
        <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-700">{RULES.map((r) => <li key={r}>{r}</li>)}</ol>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {TILES.map(([k, l, I]) => (
          <div key={k} className="bg-white rounded-2xl border border-pink-100 p-3" data-testid={`mystery-count-${k}`}>
            <I className="w-4 h-4 text-rose-500" /><p className="text-2xl font-extrabold mt-1">{d.counts[k] || 0}</p><p className="text-xs text-slate-500">{l}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-3xl border border-pink-100 p-5 space-y-3" data-testid="mystery-upcoming">
        <p className="font-bold flex items-center gap-2"><CalendarClock className="w-5 h-5 text-rose-500" /> Nächste Boxen ({d.upcoming.length} aktive Unterstützer)</p>
        {d.upcoming.length === 0 ? <p className="text-sm text-slate-500">Gerade keine aktiven Unterstützer.</p> : (
          <ul className="divide-y divide-pink-50">
            {d.upcoming.map((u) => (
              <li key={u.user.user_id} className="py-2.5 flex items-center gap-3" data-testid={`mystery-upcoming-${u.user.user_id}`}>
                <UserAvatar userId={u.user.user_id} name={u.user.name} picture={u.user.picture} size="w-9 h-9" text="text-xs" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold truncate">{u.user.name}</p>
                  <p className="text-xs text-slate-500">seit {u.streak_days} Tagen am Stück · Box #{u.next_n} am {new Date(u.next_at).toLocaleDateString("de-DE")}</p>
                  {u.at_risk && <p className="text-xs font-semibold text-amber-600">Status endet am {new Date(u.until).toLocaleDateString("de-DE")} – vorher, Box verfällt ohne Verlängerung</p>}
                </div>
                <span className="text-sm font-extrabold text-rose-600 shrink-0">in {u.days_left} {u.days_left === 1 ? "Tag" : "Tagen"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <FeedCardToggle state={d.feed_card} onDone={load} />
      <PromoButton last={d.promo_last} onDone={load} />
    </div>
  );
}
